<script lang="ts">
  /**
   * Контекстная панель настроек активного инструмента или выделенного shape.
   * Расположена справа, вертикальная. Меняется в зависимости от того,
   * что выбрано, чтобы не перегружать UI.
   */
  import { editor } from '../store/editor.svelte';
  import { BASE_DEFAULTS } from '../domain/defaults';
  import { nextNumberValue } from '../domain/numbering';
  import { theme } from '../store/theme.svelte';
  import { exportImage, exportToBlob } from '../rendering/export';
  import Icon from './Icon.svelte';
  import type { Snippet } from 'svelte';
  import type { NumberShape, Shape, ToolId } from '../domain/types';

  // Приоритет: если есть выделение — показываем свойства первого выделенного shape.
  // Иначе (при отсутствии выделения) показываем настройки фона (target = null).
  const target = $derived.by<{ kind: 'shape'; shape: Shape } | null>(() => {
    if (editor.primarySelection) {
      return { kind: 'shape', shape: editor.primarySelection };
    }
    return null;
  });

  const shape = $derived(target?.shape || null);
  const toolId = $derived(editor.activeTool);
  const toolSettings = $derived(editor.toolSettings[toolId]);
  const isTool = $derived(!shape);

  let busy = $state(false);
  let copying = $state(false);

  async function handleDownload() {
    if (!editor.project.image) return;
    busy = true;
    try {
      await exportImage(editor.project, 'png');
    } catch (err) {
      console.error(err);
      alert('Не удалось экспортировать изображение');
    } finally {
      busy = false;
    }
  }

  async function handleCopyToClipboard() {
    if (!editor.project.image) return;
    copying = true;
    try {
      const blob = await exportToBlob(editor.project, 'png');
      const item = new ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([item]);
    } catch (err) {
      console.error(err);
      alert('Не удалось скопировать изображение в буфер обмена. Пожалуйста, убедитесь, что страница активна и у браузера есть разрешение.');
    } finally {
      copying = false;
    }
  }

  function setStrokeColor(v: string) {
    if (shape) editor.updateShape(shape.id, { strokeColor: v }, false);
    else editor.setToolSetting(toolId, 'strokeColor', v);
  }
  function setStrokeWidth(v: number) {
    if (shape) editor.updateShape(shape.id, { strokeWidth: v }, false);
    else editor.setToolSetting(toolId, 'strokeWidth', v);
  }
  function setFillColor(v: string) {
    if (shape) editor.updateShape(shape.id, { fillColor: v }, false);
    else editor.setToolSetting(toolId, 'fillColor', v);
  }
  function toggleStroke() {
    if (shape) {
      editor.commit();
      editor.updateShape(shape.id, { strokeEnabled: !shape.strokeEnabled });
    } else {
      editor.setToolSetting(toolId, 'strokeEnabled', !toolSettings?.strokeEnabled);
    }
  }
  function toggleFill() {
    if (shape) {
      editor.commit();
      editor.updateShape(shape.id, { fillEnabled: !shape.fillEnabled });
    } else {
      editor.setToolSetting(toolId, 'fillEnabled', !toolSettings?.fillEnabled);
    }
  }

  function applyPaletteColor(color: string) {
    if (shape) {
      editor.commit();
      if ('strokeColor' in shape) editor.updateShape(shape.id, { strokeColor: color });
      if (shape.type === 'text' || shape.type === 'number') {
        editor.updateShape(shape.id, { textColor: color });
      }
    } else {
      editor.setToolSetting(toolId, 'strokeColor', color);
      if (toolId === 'text' || toolId === 'number') {
        editor.setToolSetting(toolId, 'textColor', color);
      }
    }
  }

  const showStroke = $derived(
    shape
      ? shape.type !== 'text' && shape.type !== 'blur' && shape.type !== 'arrow'
      : toolId !== 'select' && toolId !== 'text' && toolId !== 'blur' && toolId !== 'arrow',
  );
  const showFill = $derived(
    shape
      ? shape.type !== 'arrow' && shape.type !== 'blur'
      : toolId !== 'select' && toolId !== 'arrow' && toolId !== 'blur',
  );

  const strokeColor = $derived(shape ? shape.strokeColor : toolSettings?.strokeColor ?? BASE_DEFAULTS.strokeColor);
  const strokeWidth = $derived(shape ? shape.strokeWidth : toolSettings?.strokeWidth ?? BASE_DEFAULTS.strokeWidth);
  const fillColor = $derived(shape ? shape.fillColor : toolSettings?.fillColor ?? BASE_DEFAULTS.fillColor);
  const strokeEnabled = $derived(shape ? shape.strokeEnabled : toolSettings?.strokeEnabled ?? true);
  const fillEnabled = $derived(shape ? shape.fillEnabled : toolSettings?.fillEnabled ?? false);

  const fillMode = $derived(
    shape && shape.type === 'rect'
      ? (shape.fillMode ?? 'color')
      : (toolSettings?.fillMode ?? 'color')
  );
  const intensity = $derived(
    shape && shape.type === 'rect'
      ? (shape.intensity ?? 12)
      : (toolSettings?.intensity ?? 12)
  );

  function setFillMode(mode: 'color' | 'blur' | 'pixelize') {
    if (shape && shape.type === 'rect') {
      editor.updateShape(shape.id, { fillMode: mode }, true);
    } else if (toolId === 'rect') {
      editor.setToolSetting('rect', 'fillMode', mode);
    }
  }
  function setIntensity(v: number) {
    if (shape && shape.type === 'rect') {
      editor.updateShape(shape.id, { intensity: v }, false);
    } else if (toolId === 'rect') {
      editor.setToolSetting('rect', 'intensity', v);
    }
  }

  const title = $derived.by(() => {
    if (shape) {
      const map: Record<string, string> = {
        arrow: 'Стрелка', rect: 'Прямоугольник', pencil: 'Карандаш',
        text: 'Текст', number: 'Нумерация', blur: 'Маскировка',
      };
      return map[shape.type] ?? 'Слой';
    }
    const toolNames: Record<string, string> = {
      select: 'Выделение', arrow: 'Стрелка', rect: 'Прямоугольник / Маскировка',
      pencil: 'Карандаш', text: 'Текст', number: 'Нумерация', blur: 'Маскировка',
    };
    return toolNames[toolId] ?? 'Инструмент';
  });
</script>

<aside class="flex flex-col w-60 bg-panel border-l border-edge shrink-0 h-full overflow-hidden">
  {#if editor.project.image}
    <!-- ВЕРХНЯЯ СЕКЦИЯ: Скачать и буфер -->
    <div class="p-3 border-b border-edge flex flex-col gap-2 shrink-0 bg-panel-2/50">
      <button 
        class="btn w-full justify-center py-2 text-sm gap-2" 
        onclick={handleDownload} 
        disabled={busy}
      >
        <Icon name="download" size={16} />
        {busy ? 'Скачивание…' : 'Скачать'}
      </button>
      <button 
        class="btn w-full justify-center py-2 text-sm gap-2" 
        onclick={handleCopyToClipboard} 
        disabled={copying}
      >
        <Icon name="copy" size={16} />
        {copying ? 'Копирование…' : 'В буфер обмена'}
      </button>
    </div>

    <!-- СРЕДНЯЯ СЕКЦИЯ (СКРОЛЛ): Свойства фигур или фон -->
    <div class="flex-1 overflow-y-auto min-h-0">
      {#if shape}
        <div class="px-3 py-2.5 border-b border-edge sticky top-0 bg-panel z-10">
          <span class="label text-xs uppercase font-bold tracking-wider text-ink">{title}</span>
        </div>

        <!-- Глобальные цвета -->
        <div class="px-3 py-2.5 bg-panel border-b border-edge flex flex-col gap-2 shrink-0">
          <span class="text-[10px] uppercase font-bold text-ink-dim tracking-wider">Глобальные цвета</span>
          <div class="grid grid-cols-6 gap-2">
            {#each editor.palette as color, i (i)}
              <div 
                class="relative flex items-center justify-center aspect-square rounded-lg border border-edge cursor-pointer shadow-sm hover:scale-105 transition-all"
                style:background-color={color}
                title={`Изменить цвет: ${color}`}
              >
                <input
                  type="color"
                  value={color}
                  onpointerdown={() => editor.commit()}
                  oninput={(e) => editor.updatePaletteColor(i, e.currentTarget.value)}
                  class="absolute inset-0 w-full h-full cursor-pointer opacity-0"
                />
              </div>
            {/each}
          </div>
          {#if editor.palette.length > 0}
            <div class="flex flex-col gap-1 mt-1">
              <span class="text-[9px] uppercase font-bold text-ink-dim tracking-wider">HEX коды слоев</span>
              <div class="flex flex-wrap gap-1">
                {#each editor.palette as color, i (i)}
                  <input
                    type="text"
                    value={color.toUpperCase()}
                    onpointerdown={() => editor.commit()}
                    oninput={(e) => {
                      const val = e.currentTarget.value;
                      if (/^#[0-9A-F]{6}$/i.test(val)) {
                        editor.updatePaletteColor(i, val);
                      }
                    }}
                    class="field text-[10px] font-mono uppercase px-1.5 py-0.5 bg-panel border border-edge rounded w-16 text-center focus:outline-none focus:border-accent"
                  />
                {/each}
              </div>
            </div>
          {/if}
        </div>

        <div class="flex flex-col gap-4 p-3">
          {#if shape?.type === 'number'}
            {@render numberControls(shape)}
          {:else if shape?.type === 'text'}
            {@render textControls(shape)}
          {:else if shape?.type === 'blur'}
            {@render blurControls(shape)}
          {/if}

          {#if shape?.type === 'arrow'}
            {@render arrowControls(shape)}
          {/if}
          {#if shape?.type === 'rect'}
            {@render markerControls(shape)}
          {/if}

          {#if showStroke}
            {@render strokeRow()}
          {/if}
          {#if showFill}
            {@render fillRow()}
          {/if}
        </div>
      {:else}
        {@const bg = editor.project.background}
        <div class="px-3 py-2.5 border-b border-edge sticky top-0 bg-panel z-10">
          <span class="label text-xs uppercase font-bold tracking-wider text-ink">Фон проекта</span>
        </div>
        
        <div class="flex flex-col gap-4 p-3">
          <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
            <label class="flex items-center justify-between cursor-pointer">
              <span class="text-xs font-bold text-ink uppercase tracking-wider font-semibold">Включить фон</span>
              <input
                type="checkbox"
                checked={bg?.enabled ?? false}
                onchange={(e) => editor.updateBackground({ enabled: e.currentTarget.checked })}
                class="accent-accent w-4 h-4 cursor-pointer"
              />
            </label>
            
            {#if bg?.enabled}
              <div class="flex flex-col gap-3 pt-2 border-t border-edge/50">
                <label class="flex flex-col gap-1">
                  <span class="text-[10px] text-ink-dim uppercase tracking-wider">Цвет фона</span>
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-lg overflow-hidden border border-edge relative shrink-0 shadow-sm hover:scale-105 transition-all">
                      <input
                        type="color"
                        value={bg?.color ?? '#ffffff'}
                        onpointerdown={() => editor.commit()}
                        oninput={(e) => editor.updateBackground({ color: e.currentTarget.value })}
                        class="absolute inset-0 w-full h-full cursor-pointer opacity-0"
                      />
                      <div class="w-full h-full" style:background-color={bg?.color ?? '#ffffff'}></div>
                    </div>
                    <input
                      type="text"
                      value={(bg?.color ?? '#ffffff').toUpperCase()}
                      onpointerdown={() => editor.commit()}
                      oninput={(e) => {
                        const val = e.currentTarget.value;
                        if (/^#[0-9A-F]{6}$/i.test(val)) {
                          editor.updateBackground({ color: val });
                        }
                      }}
                      class="field flex-1 text-xs font-mono uppercase px-2 py-1 bg-panel border border-edge rounded-lg focus:outline-none focus:border-accent"
                      placeholder="#HEX"
                    />
                  </div>
                </label>

                <label class="flex flex-col gap-1">
                  <span class="text-[10px] text-ink-dim uppercase tracking-wider">Отступ (px)</span>
                  {@render widthInput(bg?.padding ?? 50, 0, 200, (v) => editor.updateBackground({ padding: v }))}
                </label>

                <label class="flex flex-col gap-1">
                  <span class="text-[10px] text-ink-dim uppercase tracking-wider font-semibold">Скругление углов (px)</span>
                  {@render widthInput(bg?.cornerRadius ?? 12, 0, 100, (v) => editor.updateBackground({ cornerRadius: v }))}
                </label>

                <label class="flex items-center justify-between cursor-pointer pt-1">
                  <span class="text-xs font-bold text-ink uppercase tracking-wider font-semibold">Тень</span>
                  <input
                    type="checkbox"
                    checked={bg?.shadowEnabled ?? false}
                    onchange={(e) => editor.updateBackground({ shadowEnabled: e.currentTarget.checked })}
                    class="accent-accent w-4 h-4 cursor-pointer"
                  />
                </label>
              </div>
            {/if}
          </div>
        </div>
      {/if}
    </div>

    <!-- НИЖНЯЯ СЕКЦИЯ: Тема и очистить всё -->
    <div class="p-3 border-t border-edge bg-panel-2/50 flex items-center justify-between shrink-0 mt-auto">
      <button
        class="btn-icon"
        title={theme.current === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        onclick={() => theme.toggle()}
        aria-label="Переключить тему"
      >
        <Icon name={theme.current === 'dark' ? 'sun' : 'moon'} size={18} />
      </button>
      
      <button
        class="btn-icon hover:text-accent"
        title="Очистить всё"
        onclick={() => {
          if (confirm('Удалить изображение и все аннотации?')) editor.clearImage();
        }}
      >
        <Icon name="trash" size={18} />
      </button>
    </div>
  {/if}
</aside>

{#snippet widthInput(value: number, min: number, max: number, onInput: (v: number) => void)}
  <div class="flex items-center gap-2">
    <input
      type="range"
      {min}
      {max}
      value={value}
      onpointerdown={() => editor.commit()}
      oninput={(e) => onInput(+e.currentTarget.value)}
      class="flex-1 accent-accent"
    />
    <input
      type="number"
      {min}
      {max}
      value={Math.round(value)}
      onpointerdown={() => editor.commit()}
      oninput={(e) => onInput(+e.currentTarget.value)}
      class="field text-xs text-center px-1 py-0.5 bg-panel border border-edge rounded w-12 focus:outline-none focus:border-accent tabular-nums"
    />
  </div>
{/snippet}

{#snippet colorField(value: string, onChange: (v: string) => void)}
  <div class="w-8 h-8 rounded-lg overflow-hidden border border-edge relative shrink-0 shadow-sm hover:scale-105 transition-all">
    <input
      type="color"
      {value}
      onpointerdown={() => editor.commit()}
      oninput={(e) => onChange(e.currentTarget.value)}
      class="absolute inset-0 w-full h-full cursor-pointer opacity-0"
    />
    <div class="w-full h-full" style:background-color={value}></div>
  </div>
{/snippet}

{#snippet strokeRow()}
  <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
    <label class="flex items-center justify-between cursor-pointer">
      <span class="text-xs font-bold text-ink uppercase tracking-wider">Контур</span>
      <input
        type="checkbox"
        checked={strokeEnabled}
        onchange={toggleStroke}
        class="accent-accent w-4 h-4 cursor-pointer"
      />
    </label>
    {#if strokeEnabled}
      <div class="flex flex-col gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-ink-dim uppercase tracking-wider">Цвет</span>
          {@render colorField(strokeColor, setStrokeColor)}
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-ink-dim uppercase tracking-wider">Толщина</span>
          {@render widthInput(strokeWidth, 1, 40, setStrokeWidth)}
        </label>
      </div>
    {/if}
  </div>
{/snippet}

{#snippet fillRow()}
  <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
    <label class="flex items-center justify-between cursor-pointer">
      <span class="text-xs font-bold text-ink uppercase tracking-wider">Заливка</span>
      <input
        type="checkbox"
        checked={fillEnabled}
        onchange={toggleFill}
        class="accent-accent w-4 h-4 cursor-pointer"
      />
    </label>
    {#if fillEnabled}
      <div class="flex flex-col gap-3">
        {#if shape?.type === 'rect' || (isTool && toolId === 'rect')}
          <div class="flex flex-col gap-1">
            <span class="text-[10px] text-ink-dim uppercase tracking-wider">Тип заливки</span>
            <div class="flex gap-1 bg-panel border border-edge rounded-lg p-0.5">
              {#each [['color', 'Цвет'], ['blur', 'Размытие'], ['pixelize', 'Пиксель']] as [mode, label]}
                <button
                  type="button"
                  class="flex-1 text-[11px] py-1 rounded-md transition-colors {fillMode === mode ? 'bg-accent text-white font-semibold' : 'text-ink-dim hover:text-ink hover:bg-panel-2'}"
                  onclick={() => setFillMode(mode as any)}
                >{label}</button>
              {/each}
            </div>
          </div>
          {#if fillMode === 'color'}
            <label class="flex flex-col gap-1">
              <span class="text-[10px] text-ink-dim uppercase tracking-wider">Цвет</span>
              {@render colorField(fillColor, setFillColor)}
            </label>
          {:else}
            <label class="flex flex-col gap-1">
              <span class="text-[10px] text-ink-dim uppercase tracking-wider">Степень</span>
              {@render widthInput(intensity, 2, 60, setIntensity)}
            </label>
          {/if}
        {:else}
          <label class="flex flex-col gap-1">
            <span class="text-[10px] text-ink-dim uppercase tracking-wider">Цвет</span>
            {@render colorField(fillColor, setFillColor)}
          </label>
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

{#snippet arrowControls(s: Shape | null)}
  {@const currentSize = s && s.type === 'arrow' ? s.headSize : (toolSettings?.headSize ?? 16)}
  {@const currentColor = s && s.type === 'arrow' ? s.strokeColor : (toolSettings?.strokeColor ?? '#ff453a')}

  <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
    {#if s && s.type === 'arrow'}
      <label class="flex items-center gap-2 text-xs text-ink cursor-pointer font-semibold">
        <input
          type="checkbox"
          checked={s.globalMode || false}
          onchange={(e) => editor.updateShape(s.id, { globalMode: e.currentTarget.checked }, true)}
          class="accent-accent w-4 h-4 cursor-pointer"
        />
        <span>Глобально</span>
      </label>
    {/if}
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Размер</span>
      {@render widthInput(currentSize, 10, 36, (v) => {
        if (s && s.type === 'arrow') editor.updateShape(s.id, { headSize: v });
        else editor.setToolSetting('arrow', 'headSize', v);
      })}
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Цвет</span>
      {@render colorField(currentColor, (val) => {
        if (s && s.type === 'arrow') editor.updateShape(s.id, { strokeColor: val });
        else editor.setToolSetting('arrow', 'strokeColor', val);
      })}
    </label>

    {#if s && s.type === 'arrow'}
      <fieldset class="flex flex-col gap-1.5">
        <span class="text-[10px] text-ink-dim uppercase tracking-wider">Маркер</span>
        <div class="flex gap-1">
          {#each [['number', '1'], ['bang', '!']] as [val, icon] (val)}
            <button
              type="button"
              class="flex-1 btn text-xs py-1.5 rounded-lg border border-edge {s.marker?.kind === val ? 'bg-[rgba(255,69,58,0.2)] border-accent font-bold' : 'bg-panel'}"
              aria-pressed={s.marker?.kind === val}
              onclick={() => {
                editor.commit();
                if (s.marker?.kind === val) {
                  editor.updateShape(s.id, { marker: null });
                } else if (val === 'bang') {
                  editor.updateShape(s.id, { marker: { kind: 'bang' } });
                } else {
                  const nextVal = nextNumberValue(editor.project.shapes);
                  editor.updateShape(s.id, { marker: { kind: 'number', value: nextVal } });
                  editor.renumberAll();
                }
              }}
              title={val === 'number' ? 'Номер' : 'Восклицание'}
            >{icon}</button>
          {/each}
        </div>
      </fieldset>

      {#if s.marker?.kind === 'number'}
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-ink-dim uppercase tracking-wider">Номер маркера</span>
          <input
            type="number"
            min="1"
            value={s.marker.value}
            class="field text-xs px-2 py-1.5 border border-edge rounded-lg bg-panel focus:outline-none focus:border-accent"
            onpointerdown={() => editor.commit()}
            oninput={(e) => editor.setNumberValue(s.id, +e.currentTarget.value, true)}
          />
        </label>
      {/if}
    {/if}
  </div>
{/snippet}

{#snippet markerControls(s: Shape | null)}
  {#if s && s.type === 'rect'}
    <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
      <fieldset class="flex flex-col gap-1.5">
        <span class="text-[10px] text-ink-dim uppercase tracking-wider">Маркер</span>
        <div class="flex gap-1">
          {#each [['number', '1'], ['bang', '!']] as [val, icon] (val)}
            <button
              type="button"
              class="flex-1 btn text-xs py-1.5 rounded-lg border border-edge {s.marker?.kind === val ? 'bg-[rgba(255,69,58,0.2)] border-accent font-bold' : 'bg-panel'}"
              aria-pressed={s.marker?.kind === val}
              onclick={() => {
                editor.commit();
                if (s.marker?.kind === val) {
                  editor.updateShape(s.id, { marker: null });
                } else if (val === 'bang') {
                  editor.updateShape(s.id, { marker: { kind: 'bang' } });
                } else {
                  const nextVal = nextNumberValue(editor.project.shapes);
                  editor.updateShape(s.id, { marker: { kind: 'number', value: nextVal } });
                  editor.renumberAll();
                }
              }}
              title={val === 'number' ? 'Номер' : 'Восклицание'}
            >{icon}</button>
          {/each}
        </div>
      </fieldset>

      {#if s.marker?.kind === 'number'}
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-ink-dim uppercase tracking-wider">Номер маркера</span>
          <input
            type="number"
            min="1"
            value={s.marker.value}
            class="field text-xs px-2 py-1.5 border border-edge rounded-lg bg-panel focus:outline-none focus:border-accent"
            onpointerdown={() => editor.commit()}
            oninput={(e) => editor.setNumberValue(s.id, +e.currentTarget.value, true)}
          />
        </label>
      {/if}
    </div>
  {/if}
{/snippet}

{#snippet textControls(s: Shape)}
  {@const ts = s as Extract<Shape, { type: 'text' }>}
  <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Текст цвет</span>
      {@render colorField(ts.textColor, (val) => editor.updateShape(ts.id, { textColor: val }))}
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Размер</span>
      {@render widthInput(ts.fontSize, 8, 200, (v) => editor.updateShape(ts.id, { fontSize: v }))}
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Шрифт</span>
      <select 
        class="field text-xs px-2 py-1.5 border border-edge rounded-lg bg-panel focus:outline-none focus:border-accent" 
        value={ts.fontFamily} 
        onchange={(e) => {
          editor.commit();
          editor.updateShape(ts.id, { fontFamily: e.currentTarget.value });
        }}
      >
        <option value="Inter, system-ui, sans-serif">Inter</option>
        <option value="'Montserrat', sans-serif">Montserrat (Google)</option>
        <option value="'Outfit', sans-serif">Outfit (Google)</option>
        <option value="'Roboto', sans-serif">Roboto (Google)</option>
        <option value="'Playfair Display', serif">Playfair Display (Google)</option>
        <option value="Arial, sans-serif">Arial</option>
        <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
        <option value="'Times New Roman', Times, serif">Times New Roman</option>
        <option value="Georgia, serif">Georgia</option>
        <option value="'Courier New', monospace">Courier New</option>
        <option value="Verdana, sans-serif">Verdana</option>
      </select>
    </label>
    <div class="flex items-center gap-1">
      <button class="btn-icon h-8 w-8 rounded-lg border border-edge bg-panel {ts.bold ? 'bg-[rgba(255,69,58,0.2)] border-accent font-bold' : ''}" onclick={() => { editor.commit(); editor.updateShape(ts.id, { bold: !ts.bold }); }}>Ж</button>
      <button class="btn-icon h-8 w-8 rounded-lg border border-edge bg-panel {ts.italic ? 'bg-[rgba(255,69,58,0.2)] border-accent font-bold' : ''}" style:font-style="italic" onclick={() => { editor.commit(); editor.updateShape(ts.id, { italic: !ts.italic }); }}>К</button>
      <button class="btn-icon h-8 w-8 rounded-lg border border-edge bg-panel {ts.underline ? 'bg-[rgba(255,69,58,0.2)] border-accent font-bold' : ''}" style:text-decoration="underline" onclick={() => { editor.commit(); editor.updateShape(ts.id, { underline: !ts.underline }); }}>Ч</button>
      <button class="btn-icon h-8 w-8 rounded-lg border border-edge bg-panel {ts.strikethrough ? 'bg-[rgba(255,69,58,0.2)] border-accent font-bold' : ''}" style:text-decoration="line-through" onclick={() => { editor.commit(); editor.updateShape(ts.id, { strikethrough: !ts.strikethrough }); }}>З</button>
    </div>
    <label class="flex items-center gap-2 text-xs text-ink cursor-pointer font-semibold">
      <input 
        type="checkbox" 
        checked={ts.hasBackground} 
        onchange={(e) => {
          editor.commit();
          editor.updateShape(ts.id, { hasBackground: e.currentTarget.checked });
        }} 
        class="accent-accent w-4 h-4 cursor-pointer" 
      />
      Плашка под текстом
    </label>
  </div>
{/snippet}

{#snippet blurControls(s: Shape)}
  {@const bs = s as Extract<Shape, { type: 'blur' }>}
  <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Эффект</span>
      <select 
        class="field text-xs px-2 py-1.5 border border-edge rounded-lg bg-panel focus:outline-none focus:border-accent" 
        value={bs.mode} 
        onchange={(e) => {
          editor.commit();
          editor.updateShape(bs.id, { mode: e.currentTarget.value as any });
        }}
      >
        <option value="pixelize">Пикселизация</option>
        <option value="blur">Размытие</option>
      </select>
    </label>
    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Степень</span>
      {@render widthInput(bs.intensity, 2, 60, (v) => editor.updateShape(bs.id, { intensity: v }))}
    </label>
  </div>
{/snippet}

{#snippet numberControls(s: Shape | null)}
  {@const ns = s as NumberShape | null}
  {@const currentRadius = ns ? ns.radius : (toolSettings?.radius ?? 22)}
  {@const currentArrowLength = ns ? ns.arrowLength : (toolSettings?.arrowLength ?? 120)}
  {@const currentHasArrow = ns ? ns.hasArrow : (toolSettings?.hasArrow !== false)}
  {@const currentTextColor = ns ? ns.textColor : (toolSettings?.textColor ?? '#ffffff')}

  <div class="flex flex-col gap-3 p-3 bg-panel-2 border border-edge rounded-xl shadow-sm">
    {#if ns}
      <label class="flex items-center gap-2 text-xs text-ink cursor-pointer font-semibold">
        <input
          type="checkbox"
          checked={ns.globalMode || false}
          onchange={(e) => editor.updateShape(ns.id, { globalMode: e.currentTarget.checked }, true)}
          class="accent-accent w-4 h-4 cursor-pointer"
        />
        <span>Глобально</span>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-ink-dim uppercase tracking-wider">Номер</span>
        <input
          type="number"
          min="1"
          value={ns.value}
          class="field text-xs px-2 py-1.5 border border-edge rounded-lg bg-panel focus:outline-none focus:border-accent"
          onpointerdown={() => editor.commit()}
          oninput={(e) => editor.setNumberValue(ns.id, +e.currentTarget.value, true)}
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-ink-dim uppercase tracking-wider">Угол стрелки</span>
        {@render widthInput(((ns.arrowAngle % 360) + 360) % 360, 0, 360, (v) => editor.updateShape(ns.id, { arrowMode: 'angle', arrowAngle: v }))}
      </label>
    {/if}

    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Размер</span>
      {@render widthInput(currentRadius, 12, 60, (v) => {
        if (ns) editor.updateShape(ns.id, { radius: v });
        else editor.setToolSetting('number', 'radius', v);
      })}
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Длина стрелки</span>
      {@render widthInput(currentArrowLength, 10, 500, (v) => {
        if (ns) editor.updateShape(ns.id, { arrowMode: 'angle', arrowLength: v });
        else editor.setToolSetting('number', 'arrowLength', v);
      })}
    </label>

    <label class="flex items-center gap-2 text-xs text-ink cursor-pointer font-semibold">
      <input
        type="checkbox"
        checked={currentHasArrow}
        onchange={(e) => {
          editor.commit();
          const val = e.currentTarget.checked;
          if (ns) editor.updateShape(ns.id, { hasArrow: val });
          else editor.setToolSetting('number', 'hasArrow', val);
        }}
        class="accent-accent w-4 h-4 cursor-pointer"
      />
      Показывать стрелку
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-[10px] text-ink-dim uppercase tracking-wider">Цвет цифры</span>
      {@render colorField(currentTextColor, (val) => {
        if (ns) editor.updateShape(ns.id, { textColor: val });
        else editor.setToolSetting('number', 'textColor', val);
      })}
    </label>
  </div>
{/snippet}
